/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1783382400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 07 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kevel",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Kevel",
          "depth": 0,
          "id": "connect-to-kevel"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Segment members",
          "depth": 1,
          "id": "segment-members"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#segment-members",
                children: _jsx(_components.strong, {
                  children: "Segment members"
                })
              })
            }), _jsx(_components.td, {
              children: "Upload a segment's full membership to Kevel as a CSV, with the segment's metadata in a leading row."
            }), _jsx(_components.td, {
              children: "All"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Kevel replaces a segment's membership in full on every import, so this destination always sends the complete audience. For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-kevel",
        children: "Connect to Kevel"
      }), "\n", _jsx(_components.p, {
        children: "Kevel provides a pre-created S3 bucket for each Kevel Audience system. Configure a folder source in Kevel to import from that bucket, then point Hightouch at the same bucket."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsx(_components.a, {
              href: "https://app.hightouch.com/destinations?from=docs",
              children: "Destinations overview"
            }), " page and select ", _jsx(_components.strong, {
              children: "Add destination"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Kevel"
            }), " from the destination catalog."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter the following, then select ", _jsx(_components.strong, {
              children: "Continue"
            }), ":"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "AWS Credentials"
              }), ": Select or create ", _jsx(_components.a, {
                href: "/security/aws",
                children: "AWS credentials"
              }), " with write access to the bucket."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Bucket name"
              }), ": The Kevel-provided S3 bucket for your Kevel Audience system, or your own bucket registered as a Kevel folder source."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Set up a folder source in Kevel pointing at this bucket so Kevel imports each\nfile Hightouch uploads. Schedule the import, or configure it to run when new\nfiles are detected."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "segment-members",
        children: "Segment members"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sync run uploads one CSV to the configured object key. The file begins with a segment-definition row that carries the segment's metadata, followed by one row per member. The sync mode is ", _jsx(_components.strong, {
          children: "All"
        }), ": every run writes the segment's complete membership, which Kevel imports as a full replacement."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsx(_components.p, {
        children: "Kevel matches members by exact identifier value and type, and never matches across types. Configure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identifier type"
          }), " — the type Kevel matches on. Hightouch writes it to the file's ", _jsx(_components.code, {
            children: "userIdType"
          }), " column:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Email"
              }), ", ", _jsx(_components.strong, {
                children: "Email (SHA-256)"
              }), ", or ", _jsx(_components.strong, {
                children: "Email (MD5)"
              })]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Phone (E.164)"
              }), " or ", _jsx(_components.strong, {
                children: "Phone (E.164, SHA-256)"
              })]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Android advertising ID (GAID)"
              }), " or ", _jsx(_components.strong, {
                children: "iOS advertising ID (IDFA)"
              })]
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.strong, {
                children: "CRM ID"
              })
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identifier column"
          }), " — the source column that holds the identifier value."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "configure-identifier-hashing",
        children: "Configure identifier hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["When you select a hashed identifier type (such as ", _jsx(_components.strong, {
          children: "Email (SHA-256)"
        }), "), Hightouch asks whether to detect and hash your data:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Yes"
          }), " — Hightouch hashes the mapped column to the selected format and leaves values that are already hashed untouched."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "No"
          }), " — your column already contains hashed values, so Hightouch sends them as-is."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Plaintext identifier types are always sent unchanged."
      }), "\n", _jsx(_components.h4, {
        id: "describe-the-segment",
        children: "Describe the segment"
      }), "\n", _jsx(_components.p, {
        children: "Kevel creates the segment on first import and updates its metadata on later imports:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment name"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Defaults to your model's name. Up to 200 characters. Kevel keys the segment on this name."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment category"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Up to 20 characters. Cannot start with \"Kevel\"."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment description"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Up to 500 characters."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Static segment"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Mark the segment as static in Kevel. Defaults to unchecked."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "set-the-object-key",
        children: "Set the object key"
      }), "\n", _jsxs(_components.p, {
        children: ["Set the ", _jsx(_components.strong, {
          children: "object key"
        }), " (file path) in the bucket where Hightouch uploads the CSV, for example ", _jsx(_components.code, {
          children: "segments/high-value-customers.csv"
        }), ". You can include timestamp variables such as ", _jsx(_components.code, {
          children: "{YYYY}"
        }), ", ", _jsx(_components.code, {
          children: "{MM}"
        }), ", and ", _jsx(_components.code, {
          children: "{DD}"
        }), " in the path."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, confirm the import in Kevel:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Check that the CSV landed at the configured object key in your bucket."
        }), "\n", _jsx(_components.li, {
          children: "In Kevel, confirm the folder source imported the file and that the segment shows the expected member count. Files uploaded to the Kevel-provided bucket are deleted automatically after one month."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
